import { EffectiveLevel } from '../../types/backend';
import type { EffectiveLevelType } from '../../types/backend';

/** A level an operator can hold: 'none' is the absence of one, never a level (ADR-0041). */
export type AssignedLevel = Exclude<EffectiveLevelType, 'none'>;

/** The level groups in cognito:groups and the level each grants (CM-UC-15). No other name carries a level. */
export const LEVEL_GROUPS: Record<string, AssignedLevel> = {
  'level-1': EffectiveLevel.L1,
  'level-2': EffectiveLevel.L2,
  admin: EffectiveLevel.ADMIN,
};

export const LEVEL_LABELS: Record<AssignedLevel, string> = {
  [EffectiveLevel.L1]: 'L1',
  [EffectiveLevel.L2]: 'L2',
  [EffectiveLevel.ADMIN]: 'Admin',
};

const LEVEL_RANK: Record<AssignedLevel, number> = {
  [EffectiveLevel.L1]: 1,
  [EffectiveLevel.L2]: 2,
  [EffectiveLevel.ADMIN]: 3,
};

/** The admin roles. opspilot-admin is a Cognito group; msreportingadmin may also come from Entra. */
export const SUPER_ADMIN_GROUPS = ['msreportingadmin', 'opspilot-admin'];

/** The one Entra custom:groups name authorization reads; every other Entra group is display-only. */
export const ENTRA_AUTHORIZATION_GROUP = 'msreportingadmin';

/**
 * The groups the OpsPilot Users editor changes: the level groups and the opspilot-admin role.
 * msreportingadmin is never offered: it is not changed in OpsPilot (CM-UC-24, CM-UC-37).
 */
export const EDITABLE_GROUPS: string[] = [...Object.keys(LEVEL_GROUPS), 'opspilot-admin'];

/** The highest level group held, or null when none is: a former country group carries no level. */
export function assignedLevelOf(groups: string[]): AssignedLevel | null {
  let best: AssignedLevel | null = null;
  for (const group of groups) {
    const level = LEVEL_GROUPS[group];
    if (level && (best === null || LEVEL_RANK[level] > LEVEL_RANK[best])) best = level;
  }
  return best;
}
